import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Rocket, Sparkles, Wrench } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const TYPES = {
    major: { label: "Major release", icon: Rocket, tone: "text-fuchsia-200 border-fuchsia-400/30 bg-fuchsia-400/10", dot: "bg-fuchsia-400" },
    feature: { label: "New feature", icon: Sparkles, tone: "text-violet-200 border-[#7C3AED]/50 bg-[#7C3AED]/15", dot: "bg-[#7C3AED]" },
    improvement: { label: "Improvement", icon: Wrench, tone: "text-emerald-200 border-emerald-400/30 bg-emerald-400/10", dot: "bg-emerald-400" },
};
const FILTERS = [["all", "All"], ["feature", "Features"], ["improvement", "Improvements"], ["major", "Major"]];

export default function ReleaseNotesPage() {
    const [data, setData] = useState(null);
    const [filter, setFilter] = useState("all");
    useEffect(() => {
        api.get("/admin/release-notes").then((r) => setData(r.data))
            .catch((e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)));
    }, []);
    const all = data?.releases || [];
    const rows = useMemo(() => all.filter((r) => filter === "all" || r.type === filter), [all, filter]);

    return (
        <div className="px-8 py-8 max-w-4xl" data-testid="release-notes-page">
            <div className="label-xs text-[#7C3AED]">Changelog</div>
            <h1 className="font-display text-4xl tracking-tight mt-2">Release notes</h1>
            <p className="text-sm text-white/50 mt-2">
                Running <span className="font-mono text-white" data-testid="release-notes-current">{data ? `v${data.current}` : "…"}</span>
                {" "}· a new feature raises the middle number (2.1.0 → 2.2.0), an improvement or fix the last one (2.2.0 → 2.2.1).
            </p>
            <div className="flex flex-wrap gap-2 mt-6 mb-8" data-testid="release-notes-filters">
                {FILTERS.map(([key, label]) => (
                    <button key={key} onClick={() => setFilter(key)} data-testid={`release-filter-${key}`}
                        className={`rounded-full border px-3 py-1 text-xs transition-colors ${filter === key ? "bg-white text-black border-white" : "border-white/10 text-white/60 hover:text-white hover:bg-white/5"}`}>
                        {label} <span className="opacity-60">{key === "all" ? all.length : all.filter((r) => r.type === key).length}</span>
                    </button>
                ))}
            </div>
            <ol className="relative border-l border-white/10 ml-2 space-y-8" data-testid="release-list">
                {rows.map((r) => <ReleaseItem key={r.version} release={r} isCurrent={r.version === data?.current} />)}
            </ol>
            {data && !rows.length && <div className="text-sm text-white/40" data-testid="release-notes-empty">No releases of this type yet.</div>}
        </div>
    );
}

function ReleaseItem({ release, isCurrent }) {
    const t = TYPES[release.type] || TYPES.improvement;
    const Icon = t.icon;
    return (
        <li className="pl-6 relative" data-testid={`release-${release.version}`}>
            <span className={`absolute -left-[5px] top-2 w-2.5 h-2.5 rounded-full ring-4 ring-[#050505] ${t.dot}`} />
            <div className="flex flex-wrap items-center gap-3">
                <span className="font-mono text-xl text-white" data-testid={`release-version-${release.version}`}>v{release.version}</span>
                <span className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[11px] ${t.tone}`} data-testid={`release-type-${release.version}`}>
                    <Icon className="w-3 h-3" strokeWidth={1.5} /> {t.label}
                </span>
                {isCurrent && <span className="rounded border border-white/20 px-2 py-0.5 text-[11px] text-white/80" data-testid="release-current-badge">current</span>}
                <span className="text-xs text-white/40 font-mono" data-testid={`release-date-${release.version}`}>{release.date}</span>
            </div>
            <div className="font-display text-base tracking-tight mt-2 text-white/90">{release.title}</div>
            <ul className="mt-2 space-y-1.5 text-sm text-white/60 list-disc pl-5">
                {release.changes.map((c, i) => <li key={i}>{c}</li>)}
            </ul>
        </li>
    );
}
